<template>
  <div class="payment-info">
    <div class="gradient-panel">

      <sfi-divider>
        <span class="center step-text"> {{ strings.titlePage }}</span>
      </sfi-divider>

      <div class="form sfi-form content-area">
        <div class="grid-x align-center">
          <div class="cell small-12">
            <div class="callout center amount-due-container">
              <!-- Case: No remaining balance (remaining balance equals 0) -->
              <div v-if="!hasRemainingBalance">
                <h2 class="due-details">$0.00</h2>
                <h6 v-html="strings.noRemainingBalance"></h6>
              </div>
              <!-- Case: No current balance (therefore no current installment) -->
              <div v-else-if="!hasCurrentBalance">
                <h2 class="due-details">$0.00</h2>
                <h6>
                  {{ strings.noCurrentBalance.noAmountDue }}<br>
                  {{ strings.noCurrentBalance.remainingBalancePart1 }}
                  <span class="remaining-balance">{{ remainingBalanceCurrency }}</span>
                  {{ strings.noCurrentBalance.remainingBalancePart2 }}
                </h6>
              </div>
              <!-- Case: Current balance and remaining balance -->
              <div v-else>
                <h2 class="due-details"> {{ currentBalanceCurrency }} </h2>
                <h6>
                  Due on {{ currentDueDate }}
                </h6>
              </div>
              <hr class="bar-line">
              <div class="policy-information"> {{ strings.policyInformation }} </div>
              <div class="grid-x align-center text-left">
                <div class="cell small-12 medium-6">
                  <p class="details-title"> {{ strings.policyNumber }} </p>
                  <p class="details-descriptions" id="policyNumberDetail"> {{ policyDetails.policyNumber }} </p>
                </div>
                <div class="cell small-12 medium-6">
                  <p class="details-title"> {{ strings.address }} </p>
                  <p class="details-descriptions" id="addressDetail">
                    {{ policyDetails.abbreviatedAddress.street }}, {{ policyDetails.abbreviatedAddress.zip }}
                  </p>
                </div>
                <div class="cell small-12">
                  <p class="details-title"> {{ strings.agency }} </p>
                  <p class="details-descriptions" id="agencyName"> {{ policyDetails.agency.name }} </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="gradient-panel-white">
      <div class="form sfi-form content-area">
        <div class="grid-x">
          <div class="cell titles-custom-style">
            {{ strings.paymentOptions.title }}
          </div>
          <div class="cell small-12 input-radio">
            <div class="radio-padding">
              <radio-button-selector
                name="paymentAmount"
                :error="$v.payment.amount.type.$error"
                :options="amountOptions"
                :optionsAnalyticsData="analyticsConstants.paymentInformation.paymentAmount"
                v-model="payment.amount.type"
                :id="'amountToPay'">
              </radio-button-selector>
              <div v-if="payment.amount.type === 'other'" class="grid-x">
                <div class="cell small-12 medium-3">
                  <label v-if="hasRemainingBalance" class="other-amount"> {{ strings.amount }} </label>
                  <inline-input-field
                    type="text"
                    label="$"
                    placeholder="0.00"
                    v-model="payment.amount.value"
                    :error="$v.payment.amount.value.$error"
                    @blur="setAmount(); $v.payment.amount.value.$touch()"
                    :id="'customAmount'">
                    <div slot="errors" id="customAmountErrorMessages" v-if="$v.payment.amount.value.$dirty && $v.payment.amount.value.$invalid">
                      <span v-if="!$v.payment.amount.value.required" class="help-text medium-no-wrap"> {{ validationMessages.otherAmount.req }} </span>
                      <span v-else-if="!$v.payment.amount.value.pattern || !$v.payment.amount.value.minValue" class="help-text"> {{ validationMessages.otherAmount.valid }} </span>
                    </div>
                  </inline-input-field>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="grid-x">
          <div class="cell titles-custom-style">
            {{ strings.paymentOptions.paymentMethod }}
          </div>
          <div class="cell small-7 medium-4 input-radio">
            <div class="radio-padding">
              <radio-button-selector
                :options="constants.paymentTypes"
                :optionsAnalyticsData="analyticsConstants.paymentInformation.paymentMethod"
                name="paymentMethod"
                v-model="payment.type"
                :id="'paymentMethod'">
              </radio-button-selector>
            </div>
          </div>
        </div>

        <div v-if="payment.type === 'Card'" class="card-info grid-x">
          <div class="cell">
            <h3> {{ strings.cardInformation.title }} </h3>
          </div>
          <div class="cell small-12 grid-x align-middle accepted-cards">
            <label class="card-titles"> {{ strings.cardInformation.cardsAccepted }} </label>
            <img alt="Visa" src="@/assets/visa-card.svg"/>
            <img alt="Mastercard" src="@/assets/master-card.svg"/>
            <img alt="Discover" src="@/assets/discover-card.svg"/>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12">
              <label class="general-title-details"> {{ strings.cardInformation.nameOnCard }}  </label>
              <inline-input-field
                :name="analyticsConstants.paymentInformation.NAME_ON_CARD"
                type="text"
                v-model="payment.cardInfo.name"
                :error="$v.payment.cardInfo.name.$error"
                @blur="$v.payment.cardInfo.name.$touch()"
                :id="'nameOnCard'">
                <div slot="errors" id="nameOnCardErrorMessages" v-if="$v.payment.cardInfo.name.$dirty && $v.payment.cardInfo.name.$invalid">
                  <span v-if="!$v.payment.cardInfo.name.required" class="help-text medium-no-wrap"> {{ validationMessages.cardFields.nameOnCard.req }} </span>
                </div>
              </inline-input-field>
            </div>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-5">
              <label class="general-title-details"> {{ strings.cardInformation.cardNumber }} </label>
              <inline-cc-input-field
                :name="analyticsConstants.paymentInformation.CARD_NUMBER"
                type="tel"
                :maxCharacters="19"
                v-model="payment.cardInfo.number"
                valueFormat="cc_number"
                :error="$v.payment.cardInfo.number.$error"
                @blur="$v.payment.cardInfo.number.$touch()"
                :id="'cardNumber'">
                <div slot="errors" id="cardNumberErrorMessages" v-if="$v.payment.cardInfo.number.$dirty && $v.payment.cardInfo.number.$invalid">
                  <span v-if="!$v.payment.cardInfo.number.required" class="help-text medium-no-wrap"> {{ validationMessages.cardFields.cardNumber.req }} </span>
                  <span v-else-if="!$v.payment.cardInfo.number.cardNumber" class="help-text medium-no-wrap"> {{ validationMessages.cardFields.cardNumber.valid }} </span>
                  <span v-else class="help-text medium-no-wrap"> {{ validationMessages.cardFields.cardNumber.other }} </span>
                </div>
              </inline-cc-input-field>
            </div>
            <div class="cell small-7 medium-4 expiration">
              <label class="general-title-details"> {{ strings.cardInformation.expiration }} </label>
              <div class="cell grid-x">
                <div class="cell small-6">
                  <div class="first">
                    <inline-select-input
                      :name="analyticsConstants.paymentInformation.EXP_MONTH"
                      v-model="payment.cardInfo.expirationMonth"
                      @change="validateExpCard()"
                      @blur="$v.payment.cardInfo.expirationMonth.$touch()"
                      placeholder="&ndash;&ndash;"
                      :error="$v.payment.cardInfo.expirationMonth.$error || this.expirationDateInvalid"
                      :options="constants.months"
                      :id="'expirationMonth'">
                    </inline-select-input>
                    <div slot="help-text" v-if="!expirationDateValidationError">
                      <span class="help-text">MM</span>
                    </div>
                  </div>
                </div>
                <div class="cell small-6">
                  <div class="last">
                    <inline-select-input
                      :name="analyticsConstants.paymentInformation.EXP_YEAR"
                      v-model="payment.cardInfo.expirationYear"
                      @change="validateExpCard()"
                      @blur="$v.payment.cardInfo.expirationYear.$touch()"
                      :options="yearsList"
                      :error="$v.payment.cardInfo.expirationYear.$error || this.expirationDateInvalid"
                      placeholder="&ndash;&ndash;"
                      :id="'expirationYear'">
                    </inline-select-input>
                    <div slot="help-text" v-if="!expirationDateValidationError">
                      <span class="help-text">YY</span>
                    </div>
                  </div>
                </div>
                <div slot="errors" id="expirationDateErrorMessages" v-if="expirationDateValidationError">
                  <span v-if="!$v.payment.cardInfo.expirationMonth.required || !$v.payment.cardInfo.expirationYear.required" class="help-text custom-error">{{ validationMessages.cardFields.expirationDate.req }}</span>
                  <span v-if="this.expirationDateInvalid" class="help-text custom-error"> {{ validationMessages.cardFields.expirationDate.valid }} </span>
                </div>
              </div>
            </div>
            <div class="cell small-5 medium-3 mobile-cvv-field-custom-padding">
              <div class="field-custom-margin">
                <label class="general-title-details"> {{ strings.cardInformation.cvv }} </label>
                <inline-input-field
                  :name="analyticsConstants.paymentInformation.CVV"
                  type="tel"
                  :maxCharacters="3"
                  v-model="payment.cardInfo.cvv"
                  helptipKey="cvv"
                  :helptipAnalyticsName="analyticsConstants.paymentInformation.CVV_HELP"
                  :error="$v.payment.cardInfo.cvv.$error"
                  @blur="$v.payment.cardInfo.cvv.$touch()"
                  :id="'cvv'">
                  <div slot="errors" id="cvvErrorMessages" v-if="$v.payment.cardInfo.cvv.$dirty && $v.payment.cardInfo.cvv.$invalid">
                    <span v-if="!$v.payment.cardInfo.cvv.required" class="help-text medium-no-wrap"> {{ validationMessages.cardFields.cvv.req }} </span>
                    <span v-else-if="!$v.payment.cardInfo.cvv.numeric || !$v.payment.cardInfo.cvv.minLength || !$v.payment.cardInfo.cvv.maxLength"
                      class="help-text"> {{ validationMessages.cardFields.cvv.valid }} </span>
                  </div>
                </inline-input-field>
              </div>
            </div>
            <hr class="hr-line">
          </div>
        </div>

        <div v-else class="bank-info grid-x">
          <div class="cell custom-margin">
            <h3> {{ strings.eCheck.title }} </h3>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-4 input-radio">
              <div class="account-type"> {{ strings.eCheck.accountType }} </div>
              <div class="radio-padding">
                <radio-button-selector
                  :options="constants.bankAccountTypes"
                  v-model="payment.bankInfo.accountType"
                  name="bankAccountType"
                  :id="'accountType'">
                </radio-button-selector>
              </div>
            </div>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-12">
              <label class="general-title-details"> {{ strings.eCheck.bankAccountHolderName }} </label>
              <inline-input-field
                type="text"
                v-model="payment.bankInfo.holderName"
                :error="$v.payment.bankInfo.holderName.$error"
                @blur="$v.payment.bankInfo.holderName.$touch()"
                :id="'holderName'">
                <div slot="errors" id="holderNameErrorMessages" v-if="$v.payment.bankInfo.holderName.$dirty && $v.payment.bankInfo.holderName.$invalid">
                  <span v-if="!$v.payment.bankInfo.holderName.required" class="help-text medium-no-wrap"> {{ validationMessages.eCheck.bankAccHolderName.req }} </span>
                </div>
              </inline-input-field>
            </div>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-12">
              <label class="general-title-details"> {{ strings.eCheck.bankName }} </label>
              <inline-input-field
                type="text"
                v-model="payment.bankInfo.bankName"
                :error="$v.payment.bankInfo.bankName.$error"
                @blur="$v.payment.bankInfo.bankName.$touch()"
                :id="'bankName'">
                <div slot="errors" id="bankNameErrorMessages" v-if="$v.payment.bankInfo.bankName.$dirty && $v.payment.bankInfo.bankName.$invalid">
                  <span v-if="!$v.payment.bankInfo.bankName.required" class="help-text medium-no-wrap"> {{ validationMessages.eCheck.bankName.req }} </span>
                </div>
              </inline-input-field>
            </div>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-6">
              <label class="general-title-details"> {{ strings.eCheck.bankRoutingNumber }} </label>
              <inline-input-field
                type="tel"
                :maxCharacters="9"
                v-model="payment.bankInfo.routing"
                :error="$v.payment.bankInfo.routing.$error"
                @blur="$v.payment.bankInfo.routing.$touch()"
                :id="'routingNumber'">
                <div slot="errors" id="routingNumberErrorMessages" v-if="$v.payment.bankInfo.routing.$dirty && $v.payment.bankInfo.routing.$invalid">
                  <span v-if="!$v.payment.bankInfo.routing.required" class="help-text medium-no-wrap"> {{ validationMessages.eCheck.bankRoutingNumber.req }} </span>
                  <span v-else-if="!$v.payment.bankInfo.routing.pattern" class="help-text medium-no-wrap"> {{ validationMessages.eCheck.bankRoutingNumber.valid }} </span>
                </div>
              </inline-input-field>
            </div>
            <div class="cell small-12 medium-6">
              <div class="field-custom-margin">
                <label class="general-title-details"> {{ strings.eCheck.bankAccountNumber }} </label>
                <inline-input-field
                  type="tel"
                  :maxCharacters="24"
                  v-model="payment.bankInfo.accountNumber"
                  :error="$v.payment.bankInfo.accountNumber.$error"
                  @blur="$v.payment.bankInfo.accountNumber.$touch()"
                  :id="'accountNumber'">
                  <div slot="errors" id="accountNumberErrorMessages" v-if="$v.payment.bankInfo.accountNumber.$dirty && $v.payment.bankInfo.accountNumber.$invalid">
                    <span v-if="!$v.payment.bankInfo.accountNumber.required" class="help-text medium-no-wrap"> {{ validationMessages.eCheck.bankAccNumber.req }} </span>
                    <span v-else-if="!$v.payment.bankInfo.accountNumber.numeric" class="help-text medium-no-wrap"> {{ validationMessages.eCheck.bankAccNumber.valid }} </span>
                  </div>
                </inline-input-field>
              </div>
            </div>
            <hr class="hr-line">
          </div>
        </div>

        <div class="grid-x">
          <div class="cell">
            <h3> {{ strings.billingInformation.title }} </h3>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-6">
              <label class="general-title-details"> {{ strings.billingInformation.firstName }} </label>
              <inline-input-field
                :name="analyticsConstants.paymentInformation.FIRST_NAME"
                type="text"
                v-model="payment.billingInfo.firstName"
                :error="$v.payment.billingInfo.firstName.$error"
                @blur="$v.payment.billingInfo.firstName.$touch()"
                :id="'firstName'">
                <div slot="errors" id="firstNameErrorMessages" v-if="$v.payment.billingInfo.firstName.$dirty && $v.payment.billingInfo.firstName.$invalid">
                  <span v-if="!$v.payment.billingInfo.firstName.required" class="help-text medium-no-wrap"> {{ validationMessages.billingInformation.firstName.req }} </span>
                </div>
              </inline-input-field>
            </div>
            <div class="cell small-12 medium-6">
              <div class="field-custom-margin">
                <label class="general-title-details"> {{ strings.billingInformation.lastName }} </label>
                <inline-input-field
                  :name="analyticsConstants.paymentInformation.LAST_NAME"
                  type="text"
                  v-model="payment.billingInfo.lastName"
                  :error="$v.payment.billingInfo.lastName.$error"
                  @blur="$v.payment.billingInfo.lastName.$touch()"
                  :id="'lastName'">
                  <div slot="errors" id="lastNameErrorMessages" v-if="$v.payment.billingInfo.lastName.$dirty && $v.payment.billingInfo.lastName.$invalid">
                    <span v-if="!$v.payment.billingInfo.lastName.required" class="help-text medium-no-wrap"> {{ validationMessages.billingInformation.lastName.req }} </span>
                  </div>
                </inline-input-field>
              </div>
          </div>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-9">
              <label class="general-title-details"> {{ strings.billingInformation.streetAddress }} </label>
              <inline-input-field
                :name="analyticsConstants.paymentInformation.STREET_ADDRESS"
                type="text"
                v-model="payment.billingInfo.address"
                :error="$v.payment.billingInfo.address.$error"
                @blur="$v.payment.billingInfo.address.$touch()"
                :id="'streetAddress'">
                <div slot="errors" id="streetAddressErrorMessages" v-if="$v.payment.billingInfo.address.$dirty && $v.payment.billingInfo.address.$invalid">
                  <span v-if="!$v.payment.billingInfo.address.required" class="help-text medium-no-wrap"> {{ validationMessages.billingInformation.address.req }} </span>
                </div>
              </inline-input-field>
            </div>
            <div class="cell small-12 medium-3">
              <div class="field-custom-margin">
                <label class="general-title-details"> {{ strings.billingInformation.aptSuite }} </label>
                <inline-input-field
                  :name="analyticsConstants.paymentInformation.APT_SUITE"
                  type="text"
                  v-model="payment.billingInfo.apt"
                  :id="'aptSuite'">
                </inline-input-field>
              </div>
          </div>
          </div>
          <div class="cell grid-x">
            <div class="cell small-12 medium-7">
              <label class="general-title-details"> {{ strings.billingInformation.city }} </label>
              <inline-input-field
                :name="analyticsConstants.paymentInformation.CITY"
                type="text"
                v-model="payment.billingInfo.city"
                :error="$v.payment.billingInfo.city.$error"
                @blur="$v.payment.billingInfo.city.$touch()"
                :id="'city'">
                <div slot="errors" id="cityErrorMessages" v-if="$v.payment.billingInfo.city.$dirty && $v.payment.billingInfo.city.$invalid">
                  <span v-if="!$v.payment.billingInfo.city.required" class="help-text medium-no-wrap"> {{ validationMessages.billingInformation.city.req }} </span>
                </div>
              </inline-input-field>
            </div>
            <div class="cell small-6 medium-2">
              <div class="field-custom-margin">
                <label class="general-title-details"> {{ strings.billingInformation.state }} </label>
                <inline-select-input
                  :name="analyticsConstants.paymentInformation.STATE"
                  v-model="payment.billingInfo.state"
                  placeholder="&ndash;&ndash;"
                  :options="constants.states"
                  :error="$v.payment.billingInfo.state.$error"
                  @blur="$v.payment.billingInfo.state.$touch()"
                  :id="'state'">
                  <div slot="errors" id="stateErrorMessages" v-if="$v.payment.billingInfo.state.$dirty && $v.payment.billingInfo.state.$invalid">
                    <span v-if="!$v.payment.billingInfo.state.required" class="help-text"> {{ validationMessages.billingInformation.state.req }} </span>
                  </div>
                </inline-select-input>
              </div>
            </div>
            <div class="cell small-6 medium-3">
              <div class="field-custom-margin mobile-custom-margin">
                <label class="general-title-details"> {{ strings.billingInformation.zipCode }} </label>
                <inline-input-field
                  :name="analyticsConstants.paymentInformation.ZIP_CODE"
                  type="text"
                  :maxCharacters="5"
                  v-model="payment.billingInfo.zip"
                  :error="$v.payment.billingInfo.zip.$error"
                  @blur="$v.payment.billingInfo.zip.$touch()"
                  :id="'zipcode'">
                  <div slot="errors" id="zipCodeErrorMessages" v-if="$v.payment.billingInfo.zip.$dirty && $v.payment.billingInfo.zip.$invalid">
                    <span v-if="!$v.payment.billingInfo.zip.required" class="help-text medium-no-wrap"> {{ validationMessages.billingInformation.zipCode.req }} </span>
                    <span v-else-if="!$v.payment.billingInfo.zip.pattern" class="help-text"> {{ validationMessages.billingInformation.zipCode.valid }} </span>
                  </div>
                </inline-input-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="gradient-panel">
      <div class="form sfi-form content-area">
        <div class="grid-x align-middle">
          <div class="cell small-12 medium-4 show-for-medium">
            <button class="button primary-blue clear" id="cancelButton" @click="cancelTransaction()" :name="analyticsConstants.CANCEL"> {{ strings.cancelButton }} </button>
          </div>
          <div class="cell small-12 medium-4 grid-x align-center">
            <button class="button primary-orange fixed expanded-for-small-only" id="reviewButton" @click="next" :disabled="$v.$invalid || this.expirationDateInvalid" :name="analyticsConstants.paymentInformation.REVIEW_PAYMENT"> {{ strings.reviewButton }} </button>
          </div>
          <div class="cell small-12 medium-3 show-for-small-only center cancel-button-mobile">
            <button class="button primary-blue clear" @click="cancelTransaction()" :name="analyticsConstants.CANCEL"> {{ strings.cancelButton }} </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Utils
import _ from 'lodash';
import moment from 'moment';
import { mapState } from 'vuex';

// Validation
import { required, numeric, email, maxLength, minLength, minValue } from 'vuelidate/lib/validators';
import pattern from './customValidation/pattern';
import cardType from './customValidation/cardType';
import cardNumber from './customValidation/cardNumber';

// Strings
import descriptions from '../i18n/en/strings';
import validationMessages from '../i18n/en/validation-messages';

// Constants
import constants from '@/i18n/en/constants';

// Services
import AnalyticsService from '@/services/AnalyticsService';

const setYearList = () => {
  let years = [];
  let currentYear;
  currentYear = new Date().getFullYear();
  for (let i = 0; i <= constants.paymentInformation.yearsToShow; i++) {
    years.push({
      key: currentYear,
      description: currentYear.toString().substr(-2)
    });
    currentYear++;
  }

  return years;
};

export default {
  name: 'PaymentInfo',
  created() {
    let validatePolicyDetails = this.$store.state.policyDetails;
    if (validatePolicyDetails.policyNumber === '' && validatePolicyDetails.abbreviatedAddress.zip === '') {
      this.redirectToPage('find-your-bill');
    }
    window.scrollTo(0, 0);
  },
  data() {
    return {
      analyticsConstants: constants.analyticsConstants,
      strings: descriptions.paymentInformation,
      constants: constants.paymentInformation,
      validationMessages: validationMessages.paymentInformation,
      policyDetails: this.$store.state.policyDetails,
      yearsList: setYearList(),
      expirationDateInvalid: false,
      payment: _.cloneDeep(this.$store.state.payment),
      getYear: new Date().getFullYear(),
      getMonth: ('0' + (new Date().getMonth() + 1)).slice(-2)
    };
  },
  mounted() {
    AnalyticsService.trackPageView(this.analyticsConstants.PAYMENT_INFORMATION);
  },
  watch: {
    'payment.amount.type': function (type) {
      if (type === 'due') {
        this.payment.amount.value = (+this.policyDetails.balanceSummary.currentInstallment.balanceDue).toFixed(2);
      } else if (type === 'remaining') {
        this.payment.amount.value = (+this.policyDetails.balanceSummary.remainingBalance).toFixed(2);
      } else {
        this.payment.amount.value = '';
      }
    }
  },
  computed: {
    ...mapState(['insuranceType']),
    // Determine if a current balance exists for the policy
    hasCurrentBalance() {
      return !!this.policyDetails.balanceSummary.currentInstallment.balanceDue;
    },
    // Determine whether the policy has a remaining balance greater than 0
    hasRemainingBalance() {
      return (this.policyDetails.balanceSummary.remainingBalance > 0);
    },
    currentBalanceCurrency() {
      return this.$options.filters.currency(this.policyDetails.balanceSummary.currentInstallment.balanceDue);
    },
    remainingBalanceCurrency() {
      return this.$options.filters.currency(this.policyDetails.balanceSummary.remainingBalance);
    },
    currentDueDate() {
      return moment(this.policyDetails.balanceSummary.currentInstallment.dueDate).format('MMMM Do, YYYY');
    },
    amountOptions() {
      let opts;
      if (!this.hasRemainingBalance) { // No remaining balance
        this.payment.amount.type = 'other';
      } else if (!this.hasCurrentBalance) { // No current balance
        if (this.$store.state.payment.amount.type !== '') {
          this.payment.amount.type = this.$store.state.payment.amount.type;
        } else {
          this.payment.amount.type = 'remaining';
        }
        opts = [
          {
            key: 'remaining',
            description: `Remaining Balance: ${this.remainingBalanceCurrency}`
          },
          {
            key: 'other',
            description: 'Custom Amount'
          }
        ];
      } else { // Current balance and remaining balance
        if (this.$store.state.payment.amount.type !== '') {
          this.payment.amount.type = this.$store.state.payment.amount.type;
        } else {
          this.payment.amount.type = 'due';
        }
        opts = [
          {
            key: 'due',
            description: `Next Amount Due: ${this.currentBalanceCurrency}`
          },
          {
            key: 'remaining',
            description: `Remaining Balance: ${this.remainingBalanceCurrency}`
          },
          {
            key: 'other',
            description: 'Custom Amount'
          }
        ];
      }
      return opts;
    },
    expirationDateValidationError() {
      return (this.$v.payment.cardInfo.expirationMonth.$dirty && this.$v.payment.cardInfo.expirationMonth.$invalid) ||
        (this.$v.payment.cardInfo.expirationYear.$dirty && this.$v.payment.cardInfo.expirationYear.$invalid) ||
        this.expirationDateInvalid;
    }
  },
  methods: {
    next() {
      if (!this.$v.$invalid) {
        this.$store.commit('ADD_PAYMENT_INFO', this.payment);
        return this.goToPage('review');
      }
    },
    // validating that the card's expiration date is not before the current date
    validateExpCard() {
      if (this.payment.cardInfo.expirationMonth === undefined && this.payment.cardInfo.expirationYear === undefined) {
        this.expirationDateInvalid = true;
      } else {
        this.expirationDateInvalid = false;
        let fullDate;
        let fullUserSeletedDate;
        fullDate = this.getYear + '-' + this.getMonth;
        if (this.payment.cardInfo.expirationMonth !== '' && this.payment.cardInfo.expirationYear !== '') {
          fullUserSeletedDate = this.payment.cardInfo.expirationYear + '-' + this.payment.cardInfo.expirationMonth;
          this.expirationDateInvalid = false;
        };
        if (fullUserSeletedDate < fullDate) {
          this.expirationDateInvalid = true;
        }
      }
    },
    // Set payment amount value to a number
    setAmount() {
      if (this.payment.amount.value !== '') {
        this.payment.amount.value = parseFloat(this.payment.amount.value).toFixed(2);
      }
    },
    cancelTransaction() {
      this.$store.dispatch('CANCEL_TRANSACTION');
      this.goToPage('find-your-bill');
    }
  },
  validations() {
    let payment = {
      amount: {
        type: { required },
        value: {
          required,
          pattern: pattern(/^\d+(\.\d{2}){1}$/),
          minValue: minValue(0.01)
        }
      },
      billingInfo: {
        firstName: { required },
        lastName: { required },
        address: { required },
        city: { required },
        state: { required },
        zip: {
          required,
          pattern: pattern(/^\d{5}(-\d{4})?$/)
        },
        email: { email }
      }
    }

    if (this.payment.type === 'Card') {
      payment.cardInfo = {
        name: {
          required
        },
        number: {
          required,
          cardType,
          cardNumber
        },
        expirationMonth: {
          required
        },
        expirationYear: {
          required
        },
        cvv: {
          required,
          numeric,
          minLength: minLength(3),
          maxLength: maxLength(3)
        }
      }
    } else {
      payment.bankInfo = {
        holderName: { required },
        bankName: { required },
        routing: {
          required,
          pattern: pattern(/^\d{9}$/)
        },
        accountNumber: {
          required,
          numeric
        }
      }
    }

    return { payment };
  }
}
</script>

<style lang="scss" type="scss" scoped>
@import '~@/styles/variables.scss';

.payment-info {

  .content-area {
    color: $black;
    position: relative;

    .amount-due-container {

      .remaining-balance {
        font-weight: $global-weight-semibold;
      }

      .due-details {
        color: $primary-color;
        text-align: center;
      }
    }

    .policy-information {
      font-size: 18px;
      font-weight: $global-weight-semibold;
      margin-bottom: 8px;
      @include breakpoint(small only) {
        text-align: left;
      }
    }

    h6 {
      font-weight: 400;
    }

    .details-title {
      font-size: $sfi-xs-font-size;
      font-weight: $global-weight-semibold;
      margin-bottom: 4px;
      margin-top: 12px;
    }

    .details-descriptions {
      font-weight: 400;
    }

    .bar-line {
      border-bottom:1px solid $medium-gray;
      margin: 32px auto 24px;
      width: 100%;
    }

    .hr-line {
      border-bottom:1px solid $medium-gray;
      margin: 32px 0 24px 0;
      width: 100%;
    }

    .custom-margin {
      margin: 0 0 20px 0;
    }

    .card-titles {
      font-size: $sfi-xs-font-size;
      font-weight: $global-weight-normal;
    }

    .general-title-details {
      font-size: $sfi-sm-font-size;
      font-weight: $global-weight-semibold;
      margin: 24px 0 8px 0;
    }

    .titles-custom-style {
      font-size: $sfi-sm-font-size;
      font-weight: $global-weight-semibold;
      margin-bottom: 8px
    }

    .account-type {
      font-size: $sfi-sm-font-size;
      font-weight: $global-weight-semibold;
      margin-bottom: 15px;
    }

    .expiration {
      @include breakpoint(medium) {
        padding-left: 10px;
      }

      .first {
        margin-right: 5px;
      }

      .last {
        margin-left: 5px;
      }
    }

    .cancel-button-mobile {
      padding-left: 0;
      margin-top: 20px;
    }

    .input-radio {
      margin-bottom: 20px;

      .radio-padding {
        padding-bottom: 4px;
      }

      .other-amount {
        font-size: $sfi-sm-font-size;
        font-weight: $global-weight-semibold;
      }
    }

    .custom-error {
      color: $alert-color;
    }

    .accepted-cards {
      margin-top: 5px;
      margin-bottom: 5px;

      label {
        margin-bottom: 0;
      }

      img {
        padding-left: 5px;
        height: 22px;
      }
    }
  }

  .field-custom-margin {
    margin-left: 10px;
  }

  .cell {
    margin-left: 0;
    margin-right: 0;
  }

  .email {
    label {
      margin: 0;
    }

    .help-text {
      color: $dark-gray;
    }
  }

  .callout {
    border: 2px solid $medium-gray;
    h1, h4 {
      margin: 0;
      font-weight: $global-weight-normal;
    }
  }

  @include breakpoint (small only) {

    .cell > .mobile-cvv-field-custom-padding {
      padding-left: 10px;
    }

    .field-custom-margin {
      margin-left: 0;
    }

    .mobile-custom-margin {
      margin-left: 10px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PaymentInfo.vue